<template>
    <div class="status-indicator">
        <div class="pulse-dot" :class="pulseClass">
            <slot name="icon">
                <div class="default-dot" :class="dotClass"></div>
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps({
    variant: {
        type: String as () => 'warning' | 'success' | 'error' | 'blue' | 'gray',
        default: 'warning'
    },
    color: {
        type: String,
        default: ''
    }
});

const pulseClass = computed(() => {
    if (props.color) {
        return 'pulse-custom';
    }
    return {
        'pulse-warning': props.variant === 'warning',
        'pulse-success': props.variant === 'success',
        'pulse-error': props.variant === 'error',
        'pulse-blue': props.variant === 'blue',
        'pulse-gray': props.variant === 'gray',
    };
});

const dotClass = computed(() => {
    return {
        'dot-warning': props.variant === 'warning',
        'dot-success': props.variant === 'success',
        'dot-error': props.variant === 'error',
        'dot-blue': props.variant === 'blue',
        'dot-gray': props.variant === 'gray',
    };
});

const customColor = computed(() => props.color || '#FFB900');
</script>

<style scoped>
.status-indicator {
    @apply relative w-5 h-5  items-center justify-center lg:flex hidden;
}

.pulse-dot {
    @apply relative flex items-center justify-center;
}

.pulse-dot :deep(svg),
.pulse-dot :deep(img) {
    @apply w-5 h-5;
}

.default-dot {
    @apply w-2 h-2 rounded-full;
}

.dot-warning {
    @apply bg-amber-400;
}

.dot-success {
    @apply bg-green-500;
}

.dot-error {
    @apply bg-red-500;
}

.dot-blue {
    @apply bg-blue-600;
}

.dot-gray {
    @apply bg-gray-600;
}

/* Warning variant (amber) */
.pulse-warning::before,
.pulse-warning::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 border-amber-400 absolute top-1/2 left-1/2;
}

/* Success variant (green) */
.pulse-success::before,
.pulse-success::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 border-green-500 absolute top-1/2 left-1/2;
}

/* Error variant (red) */
.pulse-error::before,
.pulse-error::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 border-red-500 absolute top-1/2 left-1/2;
}

/* Blue variant */
.pulse-blue::before,
.pulse-blue::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 border-blue-600 absolute top-1/2 left-1/2;
}

/* Gray variant */
.pulse-gray::before,
.pulse-gray::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 border-gray-600 absolute top-1/2 left-1/2;
}

/* Custom color variant */
.pulse-custom::before,
.pulse-custom::after {
    content: '';
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 absolute top-1/2 left-1/2;
    border-color: v-bind(customColor);
}

.pulse-dot::before {
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.pulse-dot::after {
    animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    animation-delay: 0.6s;
}

.pulse-dot:has(svg)::before,
.pulse-dot:has(img)::before,
.pulse-dot:has(svg)::after,
.pulse-dot:has(img)::after {
    animation-name: pulse-ring-with-icon;
}

@keyframes pulse-ring {
    0% {
        @apply w-full h-full opacity-100;
    }
    100% {
        @apply w-[300%] h-[300%] opacity-0;
    }
}

@keyframes pulse-ring-with-icon {
    0% {
        @apply w-full h-full opacity-100;
    }
    100% {
        @apply w-[200%] h-[200%] opacity-0;
    }
}
</style>
